/**
 * The small slice of markdown the assistant actually produces: **bold** and pipe tables.
 * Parsed into a structure the components render as real elements - nothing is ever handed to
 * v-html, so no escaping step exists to forget.
 */
export type Span = { text: string; bold: boolean };

export type Block =
    | { type: 'text'; lines: Span[][] }
    | { type: 'table'; head: Span[]; rows: Span[][][] };

export function parseSpans(value: string): Span[] {
    const spans: Span[] = [];
    const pattern = /\*\*(.+?)\*\*/g;
    let index = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(value)) !== null) {
        if (match.index > index) {
            spans.push({ text: value.slice(index, match.index), bold: false });
        }
        spans.push({ text: match[1], bold: true });
        index = match.index + match[0].length;
    }

    if (index < value.length) {
        spans.push({ text: value.slice(index), bold: false });
    }

    return spans;
}

export function parseMessage(value: string): Block[] {
    const normalized = String(value ?? '').replace(/\r\n/g, '\n');

    if (!normalized.includes('|')) {
        return [textBlock(normalized)];
    }

    // Standalone code fences would otherwise show up as literal ``` around a table.
    const lines = normalized.split('\n').filter((line) => !/^\s*```.*$/.test(String(line ?? '').trim()));
    const blocks: Block[] = [];
    let index = 0;

    while (index < lines.length) {
        if (isTableStart(lines, index)) {
            const head = splitRow(lines[index]);
            const rows: string[][] = [];
            let pointer = index + 2;

            while (pointer < lines.length && lines[pointer].includes('|')) {
                const row = splitRow(lines[pointer]);
                if (row.length > 0) {
                    rows.push(row);
                }
                pointer++;
            }

            blocks.push({
                type: 'table',
                head: head.map((cell) => ({ text: cell, bold: false })),
                rows: rows.map((row) => head.map((_, cell) => parseSpans(row[cell] ?? ''))),
            });
            index = pointer;
            continue;
        }

        const paragraph: string[] = [];
        while (index < lines.length && !isTableStart(lines, index)) {
            paragraph.push(lines[index]);
            index++;
        }

        const text = paragraph.join('\n').trim();
        if (text !== '') {
            blocks.push(textBlock(text));
        }
    }

    return blocks;
}

function textBlock(value: string): Block {
    return { type: 'text', lines: value.split('\n').map((line) => parseSpans(line)) };
}

function isTableStart(lines: string[], index: number): boolean {
    if (index + 1 >= lines.length || !lines[index].includes('|')) {
        return false;
    }

    const cells = splitRow(lines[index + 1]);

    return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function splitRow(line: string): string[] {
    const raw = String(line ?? '').trim();
    if (!raw.includes('|')) {
        return [];
    }

    return raw.replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}
